import * as Sentry from '@sentry/react';
import { load } from '@zappar/mediarecorder';
import { useEffect, useRef, useState } from 'react';
import { Jute } from '@/index';
import { useRefState } from '@/hooks';
import { dataURItoFileBlob, useCheckBrowserShareAndDowloadAbility } from '@/utils';
import { getAnalyticsLabelForAction } from '@/utils/analytics';
import { IEntityActionTypes, IUserActions, useActionStateStore, useDataStore, useSceneStore, useUserStore } from '@/zustand';
import { IActionCategory, IButtonSubCategory, ISceneComp, ITrackingTypes } from '@/components/r3f-components/component-data-structure';
import { getIsSceneHasTakePhotoActionEntity, getSnapshotButtonId, getSnapshotButtonType, isAndroid, isMultipleOfTwo } from '@/components/r3f-components/utils/general';
import * as CSS from './SnapshotUI.scss';
import BackIcon from './backIcon.svg';
import DownloadIcon from './downloadIcon.svg';
import ShareIcon from './shareIcon.svg';
import { MediaRecorder } from '@zappar/mediarecorder';
import { convertTimeDifferenceToString, createRecordingStream } from './utils';

load(() => {
	return new (require(`worker-loader?inline=fallback&filename=[name].[contenthash].worker.js!@zappar/mediarecorder/lib/converter-worker`).default)()
})

const debug = false;

enum ISnapshotState {
	'idle' = 'idle',
	'hold' = 'hold',
	'holdComplete' = 'holdComplete',
	'recording' = 'recording',
	'recordingComplete' = 'recordingComplete',
	'photoTaken' = 'photoTaken',
	'hidden' = 'hidden',
}

const SnapshotUI = () => {
	const pointerDownTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
	const holdingTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
	const dataChunksRef = useRef<Blob[] | null>(null);
	const [objectUrl, setObjectUrl] = useState<string>();
	const userDispatch = useUserStore((state) => state.dispatch);
	const entityActionDispatch = useActionStateStore(({ dispatch }) => dispatch);
	const placementMode = useSceneStore((state) => state.placementMode);
	const isFaceTrackingVisible = useUserStore(({ isFaceTrackingVisible }) => isFaceTrackingVisible);
	const isTrackingImageVisible = useUserStore(({ isTrackingImageVisible }) => isTrackingImageVisible);
	const activeSceneId = useSceneStore((state) => state.activeSceneId);
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	const activeSceneType = componentsById && activeSceneId ? (componentsById[activeSceneId] as ISceneComp).trackingType : null;
	const snapshotButtonId = getSnapshotButtonId(componentsById, activeSceneId);
	const isSceneHasSnapshotButton = !!snapshotButtonId;
	const sceneCameraButtonType = getSnapshotButtonType(componentsById, snapshotButtonId);
	const isSaveShareUiVisible = useUserStore((state) => state.isSnapshotUiVisible);
	const isSceneHasTakePhotoActionEntity = getIsSceneHasTakePhotoActionEntity(componentsById, activeSceneId);
	const [snapshotState, setSnapshotState, snapshotStateRef] = useRefState(isSceneHasSnapshotButton ? ISnapshotState.idle : ISnapshotState.hidden);
	const videoRecordingEnabled = sceneCameraButtonType === IButtonSubCategory.recording;
	let browserInfo = useCheckBrowserShareAndDowloadAbility();
	let timerIntervalRef = useRef<ReturnType<typeof setInterval>>(undefined);

	const noTrackingActive =
		(activeSceneType === ITrackingTypes.world && placementMode) ||
		(activeSceneType === ITrackingTypes.face && !isFaceTrackingVisible) ||
		(activeSceneType === ITrackingTypes.image && !isTrackingImageVisible);
	if (debug) console.info('SnapshotUI > snapshotState: ', snapshotState);

	// Canvas & MediaRecorder
	const canvasWrapperDiv = document.getElementById('zapparCanvas') as HTMLDivElement;
	const canvas = canvasWrapperDiv.getElementsByTagName('canvas')[0];
	const canvasDiv = canvas.closest('div');
	const recorderRef = useRef<MediaRecorder | null>(null);

	const initialiseMediaRecorder = async () => {
		const originalCanvasHeight = canvas.height;
		const originalCanvasWidth = canvas.width;
		if (isAndroid()) {
			const canvasHeight = Math.floor(canvas.height);
			const canvasWidth = Math.floor(canvas.width);
			if (!isMultipleOfTwo(canvasWidth)) canvas.width = canvasWidth - 1;
			if (!isMultipleOfTwo(canvasHeight)) canvas.height = canvasHeight - 1;
		}	
		const stream = createRecordingStream(canvas)
		recorderRef.current = new MediaRecorder(stream, { mimeType: 'video/mp4' });
		
		const setupRecorderEvents = () => {
			if (objectUrl) URL.revokeObjectURL(objectUrl);
			if (!recorderRef.current) return;
			recorderRef.current.onerror = (event) => {
				Sentry.captureException(event);
				console.error(event);
			};
			recorderRef.current.ondataavailable = (event) => {
				dataChunksRef.current = [];
				dataChunksRef.current.push(event.data);
				const urlSrc = URL.createObjectURL(dataChunksRef.current[0]);
				setObjectUrl(urlSrc);
			};
			recorderRef.current.onstart = () => {
				showRecordingTimer();
			}
			if (isAndroid()) {
				recorderRef.current.onstop = () => {
					canvas.height = originalCanvasHeight;
					canvas.width = originalCanvasWidth;
				}
			}
		};
		setupRecorderEvents();
	};

	const updateRecording = () => {
		setTimeout(() => {
			if (debug) console.info('Snapshot UI > updateRecording > setting snapshot state to : ', ISnapshotState.recordingComplete);
			setTimeout(() => {
				setSnapshotState(ISnapshotState.recordingComplete);
			},500)
			Jute.logEventToAnalytics({
				category: 'Video',
				name: `Video recording taken`,
				label: `Video recording taken`,
				value: 1,
			});
		}, 50);
	};

	const shareButtonAction = async (ev: PointerEvent) => {
		if (debug) console.log('Share Clicked', ev);
		const fileName = snapshotState === ISnapshotState.recordingComplete ? 'An augmented reality experience.mp4' : 'An augmented reality experience.jpeg';
		const imageFile = await dataURItoFileBlob(objectUrl as string, fileName);
		if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [imageFile] })) {
			navigator
				.share({
					files: [imageFile],
				})
				.then(() => console.log('successful share'))
				.catch((error) => console.error(error));
		}
	};

	const backButtonAction = () => {
		if (debug) console.info('SnapshotUI > backButtonAction');
		userDispatch({
			type: IUserActions.SET_PLACEMENT_MODE_UI_VISIBLE,
			payload: true,
		});
		userDispatch({
			type: IUserActions.SET_SNAPSHOT_UI_VISIBLE,
			payload: false,
		});

		const backButtonContainerDiv = document.getElementById('backButtonContainerDiv');
		const saveButtonContainer = document.getElementById('saveButtonContainer');
		const shareButtonContainerDiv = document.getElementById('shareButtonContainerDiv');
		const snapshotImageVideoContainer = document.getElementById('snapshotImageVideoContainer');
		let snapshotImage = document.getElementById('snapshotImage') as HTMLElement | null | undefined;
		let snapshotVideo = document.getElementById('snapshotVideo') as HTMLElement | null | undefined;
		const flashAnimDiv = document.getElementById('flashAnimDiv');
		const filesAppButton = document.getElementById('filesAppButton');
		if (backButtonContainerDiv) backButtonContainerDiv.remove();
		if (saveButtonContainer) saveButtonContainer.remove();
		if (shareButtonContainerDiv) shareButtonContainerDiv.remove();
		if (snapshotImage) snapshotImage.remove();
		if (snapshotVideo) snapshotVideo.remove();
		if (filesAppButton) filesAppButton.remove();
		if (flashAnimDiv) flashAnimDiv.remove();
		if (snapshotImageVideoContainer) snapshotImageVideoContainer.remove();

		const snapshotButtonInner = document.querySelector('#snapshotButtonInner');
		if (snapshotButtonInner) {
			snapshotButtonInner.className = CSS.SnapshotButtonInner;
		}

		snapshotImage = undefined;
		snapshotVideo = undefined;
		if (objectUrl) URL.revokeObjectURL(objectUrl);
		recorderRef.current = null;
		dataChunksRef.current = null;
		setObjectUrl(undefined);
		setSnapshotState(isSceneHasSnapshotButton ? ISnapshotState.idle : ISnapshotState.hidden);
		if (debug) console.info('SnapshotUI > setting objectUrl to undefined and snapshotState to idle');
	};

	const updateSnapShot = async () => {
		if (debug) console.info('Snapshot UI > updateSnapShot');
		// This needs to be before the timeout, to allow the useGetEntityIsHidden hook to run in the HOCs and hide those entities
		// that should be hidden before a snapshot is taken
		userDispatch({
			type: IUserActions.SET_SNAPSHOT_UI_VISIBLE,
			payload: true,
		});
		setTimeout(() => {
			// requestAnimationFrame() used to delay image capture until the next browser paint, otherwise image is black
			requestAnimationFrame(() => {
				setObjectUrl(canvas.toDataURL('image/jpeg', 1.0));
				userDispatch({
					type: IUserActions.SET_PLACEMENT_MODE_UI_VISIBLE,
					payload: false,
				});
				if (debug) console.info('Snapshot UI > updateSnapShot > setting snapshot state to : ', ISnapshotState.photoTaken);
				setSnapshotState(ISnapshotState.photoTaken);
			});
		}, 50);
		Jute.logEventToAnalytics({
			category: 'Image',
			name: `Snapshot taken`,
			label: getAnalyticsLabelForAction(IActionCategory.takePhoto, 'Snapshot taken'),
			value: 1,
		});
	};

	const _onPointerDown = (e: PointerEvent) => {
		e.stopPropagation();
		e.preventDefault();
		initialiseMediaRecorder();
		(e.target as Element).setPointerCapture(e.pointerId);
		if (debug) console.info('SnapshotUI > onPointerDown');
		pointerDownTimeoutRef.current = setTimeout(() => {
			if (debug) console.info('SnapshotUI > Setting current state to : ', ISnapshotState.hold);
			const snapshotButtonInner = document.querySelector('#snapshotButtonInner');
			if (!snapshotButtonInner) return;
			snapshotButtonInner.className = CSS.SnapshotButtonInnerPointerDown;
			setSnapshotState(ISnapshotState.hold);
		}, 200);
	};

	const _onPointerEnter = (_e: PointerEvent) => {
		if (debug) console.log('SnapshotUI > Enter');
		const snapshotButton = document.querySelector('#snapshotButton');
		if (!snapshotButton) return;
		const snapshotButtonInner = document.querySelector('#snapshotButtonInner');
		if (!snapshotButtonInner) return;
		snapshotButton.className = CSS.SnapshotButtonHover;
		snapshotButtonInner.className = CSS.SnapshotButtonInnerHover;
	};

	const _onPointerLeave = (_e: PointerEvent) => {
		if (debug) console.log('SnapshotUI > Leave');
		const snapshotButton = document.querySelector('#snapshotButton');
		if (!snapshotButton) return;
		const snapshotButtonInner = document.querySelector('#snapshotButtonInner');
		if (!snapshotButtonInner) return;
		snapshotButton.className = CSS.SnapshotButton;
		snapshotButtonInner.className = CSS.SnapshotButtonInner;
	};

	const _onPointerUp = (e: PointerEvent) => {
		if (debug) console.info('SnapshotUI > onPointerUp snapshotState: ', snapshotStateRef.current);
		if (pointerDownTimeoutRef.current) clearTimeout(pointerDownTimeoutRef.current);
		if (holdingTimeoutRef.current) clearTimeout(holdingTimeoutRef.current);

		if (snapshotStateRef.current === ISnapshotState.recording) {
			if (debug) console.info('SnapshotUI > Video recording complete -> updateRecording');
			recorderRef.current?.stop();
			updateRecording();
			hideRecordingtimer();
		} else {
			// If we weren't in a recording state, its a tap event - take canvas pic
			if (debug) console.info('SnapshotUI > Image snapshot complete -> updateSnapshot');
			updateSnapShot();
		}
		(e.target as Element).releasePointerCapture(e.pointerId);
	};

	const showSnapshotUi = () => {
		if (debug) console.info('SnapshotUI > showSnapshotUi');

		// Check if already shown and return early if so
		if (!!document.querySelector('#snapshotButtonContainerDiv')) return;

		// Add either tap or record ui
		const snapshotButtonContainerDiv = document.createElement('div');
		snapshotButtonContainerDiv.id = 'snapshotButtonContainerDiv';
		snapshotButtonContainerDiv.className = CSS.SnapshotButtonContainerDiv;

		const snapshotButton = document.createElement('div');
		snapshotButton.id = 'snapshotButton';
		snapshotButton.className = CSS.SnapshotButton;
		snapshotButton.addEventListener('pointerdown', _onPointerDown);
		snapshotButton.addEventListener('pointerup', _onPointerUp);
		snapshotButton.addEventListener('pointerenter', _onPointerEnter);
		snapshotButton.addEventListener('pointerleave', _onPointerLeave);

		if (videoRecordingEnabled) {
			const snapshotButtonInner = document.createElement('div');
			snapshotButtonInner.id = 'snapshotButtonInner';
			snapshotButtonInner.className = CSS.SnapshotButtonInner;
			snapshotButton.appendChild(snapshotButtonInner);

			const snapshotButtonRecordText = document.createElement('div');
			snapshotButtonRecordText.id = 'snapshotButtonRecordText';
			snapshotButtonRecordText.innerText = 'Hold to Record';
			snapshotButtonRecordText.className = CSS.SnapshotButtonRecordText;
			snapshotButtonContainerDiv.appendChild(snapshotButtonRecordText);
		}

		snapshotButtonContainerDiv.appendChild(snapshotButton);
		canvasDiv?.appendChild(snapshotButtonContainerDiv);
	};

	const showRecordingTimer = () => {
		const snapshotButtonContainerDiv = document.querySelector('#snapshotButtonContainerDiv');
		const timerContainerDiv = document.createElement('div');
		timerContainerDiv.id = 'timerContainerDiv';
		timerContainerDiv.className = CSS.TimerContainerDiv;
		const timerTextSpan = document.createElement('span');

		const startTime = new Date().getTime();
		clearInterval(timerIntervalRef.current);

		timerIntervalRef.current = setInterval(function() {
			timerTextSpan.innerHTML = convertTimeDifferenceToString(startTime, new Date().getTime());
		}, 100);

		timerContainerDiv?.appendChild(timerTextSpan);
		snapshotButtonContainerDiv?.appendChild(timerContainerDiv);
		timerContainerDiv.classList.add(CSS.TimerFadeIn);
	}

	const hideRecordingtimer = () => {
		clearInterval(timerIntervalRef.current);
		const timerContainerDiv = document.getElementById('timerContainerDiv');
		timerContainerDiv?.remove();
	}

	const showSaveShareUi = () => {
		if (debug) console.info('SnapshotUI > showSaveShareUi');
		if (debug) console.info('SnapshotUI > showSaveShareUi > snapshotState: ', snapshotState);
		if (debug) console.info('SnapshotUI > showSaveShareUi > objectUrl: ', objectUrl);
		const alreadyShowingSnapshot = !!document.getElementById('snapshotImage');
		if (alreadyShowingSnapshot) return;
		if (!objectUrl) return; // Shouldn't happen

		// Hide the snapshot / recording button
		const snapshotButton = document.querySelector('#snapshotButtonContainerDiv');
		if (snapshotButton) snapshotButton.remove();

		// There has been a screenshot - display back, save, share icons
		const flashAnimDiv = document.createElement('div');
		flashAnimDiv.className = CSS.FlashAnimDiv;
		flashAnimDiv.id = 'flash-animation-div';
		canvasDiv?.appendChild(flashAnimDiv);
		setTimeout(() => {
			canvasDiv?.removeChild(flashAnimDiv);
		}, 210);

		const backButtonContainerDiv = document.createElement('div');
		backButtonContainerDiv.id = 'backButtonContainerDiv';
		backButtonContainerDiv.className = CSS.BackButtonContainerDiv;

		const backButton = document.createElement('img');
		backButton.src = BackIcon;
		backButton.className = CSS.BackButton;
		backButtonContainerDiv.appendChild(backButton);
		backButtonContainerDiv.addEventListener('pointerdown', (e) => e.stopPropagation());
		backButtonContainerDiv.addEventListener('pointerup', (e) => e.stopPropagation());
		backButtonContainerDiv.addEventListener('click', (e) => {
			e.stopPropagation();
			backButtonAction();
			entityActionDispatch?.({
				type: IEntityActionTypes.UNMUTE_EXPERIENCE
			});
			entityActionDispatch?.({
				type: IEntityActionTypes.PLAY_BACKGROUND_AUDIO,
			});

			if (!activeSceneId || !componentsById) return;
			const activeScene = componentsById[activeSceneId] as ISceneComp;
			const activeSceneHasOnloadPlaySoundAction = (activeScene.actions?.onLoad?.filter(action => action.type === IActionCategory.playSound) ?? []).length > 0;

			if (!activeSceneHasOnloadPlaySoundAction) return;

			entityActionDispatch?.({
				type: IEntityActionTypes.PLAY_ALL_SCENE_AUDIO_BY_SCENE_AND_ENTITY_ID,
				payload: {
					sceneId: activeSceneId,
					entityIds:[activeSceneId]
				}
			});
		});

		const saveButtonContainer = document.createElement('a');
		saveButtonContainer.id = 'saveButtonContainer';
		saveButtonContainer.className = CSS.SaveButtonContainer;
		saveButtonContainer.download = snapshotState === ISnapshotState.recordingComplete ? 'An augmented reality experience.mp4' : 'An augmented reality experience.jpeg';
		saveButtonContainer.href = objectUrl || '';
		saveButtonContainer.target = '_blank';

		const saveButton = document.createElement('img');
		saveButton.src = DownloadIcon;
		saveButtonContainer.appendChild(saveButton);

		// check if browser can share files (data.files param)
		const testFile = new File([new Blob(['test'], { type: 'text/plain' })], 'test.txt');
		// hide share button if the browser cannot share files
		if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [testFile] }) && !browserInfo.iOSFilesAppForPhotoShare) {
			const shareButtonContainerDiv = document.createElement('div');
			shareButtonContainerDiv.id = 'shareButtonContainerDiv';
			shareButtonContainerDiv.className = CSS.ShareButtonContainerDiv;
			shareButtonContainerDiv.ongotpointercapture = shareButtonAction;

			const shareButton = document.createElement('img');
			shareButton.src = ShareIcon;
			shareButtonContainerDiv.appendChild(shareButton);
			canvasDiv?.appendChild(shareButtonContainerDiv);
		}

		// Container div for image / video
		const snapshotImageVideoContainer = document.createElement('div');
		snapshotImageVideoContainer.id = 'snapshotImageVideoContainer';
		snapshotImageVideoContainer.className = CSS.SnapshotImageVideoContainer;
		snapshotImageVideoContainer.onpointerdown = (e) => {
			// Prevent pointer events on the snapshot from reaching the canvas ( and triggering actions )
			e.preventDefault();
			e.stopPropagation();
		};
		canvasDiv?.appendChild(snapshotImageVideoContainer);

		if (snapshotState === ISnapshotState.photoTaken) {
			const snapshotImage = document.createElement('div');
			snapshotImage.id = 'snapshotImage';
			snapshotImage.style.backgroundImage = `url(${objectUrl})`;
			snapshotImage.className = CSS.SnapshotImage;
			snapshotImageVideoContainer?.appendChild(snapshotImage);
		}

		if (snapshotState === ISnapshotState.recordingComplete) {
			const snapshotVideo = document.createElement('video');
			snapshotVideo.id = 'snapshotVideo';
			snapshotVideo.src = objectUrl;
			snapshotVideo.autoplay = true;
			//snapshotVideo.controls = !isSafari();
			snapshotVideo.disablePictureInPicture = true;
			snapshotVideo.playsInline = true;
			snapshotVideo.preload = 'metadata';
			snapshotVideo.className = CSS.SnapshotVideo;
			snapshotImageVideoContainer?.appendChild(snapshotVideo);
			snapshotVideo.controls = true;
		}

		entityActionDispatch?.({
			type: IEntityActionTypes.MUTE_EXPERIENCE
		});

		// if not safari >= 13 show save button, otherwise hide it
		// due to issues with saving files.
		if (!browserInfo.longPressForImageShare) {
			canvasDiv?.appendChild(saveButtonContainer);
		} else {
			const shareButtonContainerDiv = document.getElementById('shareButtonContainerDiv');
			if (shareButtonContainerDiv) shareButtonContainerDiv.style.left = '5%';
		}

		canvasDiv?.appendChild(backButtonContainerDiv);
	};

	const hide = () => {
		if (debug) console.info('Snapshot UI > hide');
		// Remove any ui that is present
		const snapshotButtonContainerDiv = document.querySelector('#snapshotButtonContainerDiv');
		if (snapshotButtonContainerDiv) {
			snapshotButtonContainerDiv.remove();
		}
		// And cleanup any image / recording
	};

	const holdAction = () => {
		if (debug) console.info('SnapshotUI > holdAction');
		holdingTimeoutRef.current = setTimeout(() => {
			if (debug) console.info('SnapshotUI > Setting current state to: ', ISnapshotState.holdComplete);
			setSnapshotState(ISnapshotState.holdComplete);
		}, 150);
	};

	const holdCompleteAction = () => {
		if (recorderRef.current && videoRecordingEnabled) {
			userDispatch({
				type: IUserActions.SET_SNAPSHOT_UI_VISIBLE,
				payload: true,
			});
			recorderRef.current.start();
			if (debug) console.info('SnapshotUI > Setting current state to: ', ISnapshotState.recording);
			setSnapshotState(ISnapshotState.recording);
			setTimeout(() => {
				const snapshotButtonRecordText = document.querySelector('#snapshotButtonRecordText');
				if (snapshotButtonRecordText) {
					snapshotButtonRecordText.className = CSS.SnapshotButtonRecordTextFadeOut;
				}
			}, 100);
		}
	};

	useEffect(() => {
		if (noTrackingActive || !isSceneHasSnapshotButton) {
			if (debug) console.info('SnapshotUI > useEffect > noTrackingActive change to : ', noTrackingActive, ' snapshot state: ', ISnapshotState.hidden);
			setSnapshotState(ISnapshotState.hidden);
		} else {
			if (snapshotState === ISnapshotState.hidden && isSceneHasSnapshotButton) {
				if (debug) console.info('SnapshotUI > useEffect > noTrackingActive change to : ', noTrackingActive, ' snapshot state: ', ISnapshotState.idle);
				setSnapshotState(ISnapshotState.idle);
			}
		}
	}, [noTrackingActive, isSceneHasSnapshotButton, snapshotState]);

	useEffect(() => {
		if (!objectUrl && isSaveShareUiVisible && !videoRecordingEnabled && isSceneHasSnapshotButton) {
			if (debug) console.log('SnapshotUI > updateSnapShot() being called - by snapshot');
			updateSnapShot();
		}
	}, [objectUrl, isSaveShareUiVisible]);

	useEffect(() => {
		if (!objectUrl && isSaveShareUiVisible && !isSceneHasSnapshotButton && isSceneHasTakePhotoActionEntity) {
			if (debug) console.log('SnapshotUI > updateSnapShot() being called - by entity in scene');
			updateSnapShot();
		}
	}, [objectUrl, isSaveShareUiVisible]);

	useEffect(() => {
		if (debug) console.info(' > SnapshotUI > snapshotState changed, now: ', snapshotState);
		switch (snapshotState) {
			case ISnapshotState.hidden:
				hide();
				break;
			case ISnapshotState.idle:
				showSnapshotUi();
				break;
			case ISnapshotState.photoTaken:
				userDispatch({
					type: IUserActions.SET_SNAPSHOT_UI_VISIBLE,
					payload: true,
				});
				showSaveShareUi();
				break;
			case ISnapshotState.recordingComplete:
				if (!objectUrl) return;

				showSaveShareUi();
				break;
			case ISnapshotState.hold:
				holdAction();
				break;
			case ISnapshotState.holdComplete:
				holdCompleteAction();
				break;
			case ISnapshotState.recording:
				// recording

				break;
		}
	}, [snapshotState, objectUrl]);

	return null;
};

export default SnapshotUI;
